import { FILTER_RECORD_TYPE_ELEMENT_ID } from '../../taskpane/record-type/record-type-field';
import {
    MATERIAL_SUMMARY_FROM_DATE_ELEMENT_ID,
    MATERIAL_SUMMARY_RECORD_COUNT_ALLOWED,
    MATERIAL_SUMMARY_RECORD_COUNT_ELEMENT_ID,
    MATERIAL_SUMMARY_RECORD_TYPES,
    MATERIAL_SUMMARY_REPORT_DATE_ELEMENT_ID,
    MATERIAL_SUMMARY_TO_DATE_ELEMENT_ID,
} from '../constants/material-summary.constants';
import type { MaterialSummaryReportFilter } from '../models/material-summary.model';

const ALLOWED_RECORD_TYPES_LOWER = new Set(
    MATERIAL_SUMMARY_RECORD_TYPES.map((t) => t.toLowerCase())
);

const MATERIAL_SUMMARY_DATE_FIELD_IDS = [
    MATERIAL_SUMMARY_REPORT_DATE_ELEMENT_ID,
    MATERIAL_SUMMARY_FROM_DATE_ELEMENT_ID,
    MATERIAL_SUMMARY_TO_DATE_ELEMENT_ID,
] as const;

export type MaterialSummaryFilterValidation =
    | { ok: true }
    | { ok: false; message: string; invalidElementIds: readonly string[] };

/**
 * Client-side validation for {@link MaterialSummaryReportFilter}, aligned with Trace
 * `DashboardController.GetMaterialSummaryReport` rules.
 */
export function validateMaterialSummaryFilter(
    filter: MaterialSummaryReportFilter
): MaterialSummaryFilterValidation {
    const recordTrimmed = filter.recordType?.trim();
    if (recordTrimmed) {
        if (!ALLOWED_RECORD_TYPES_LOWER.has(recordTrimmed.toLowerCase())) {
            return {
                ok: false,
                message: 'recordType must be receipt, dispatched, or processed.',
                invalidElementIds: [FILTER_RECORD_TYPE_ELEMENT_ID],
            };
        }
    }

    const hasDate = hasNonEmptyDateString(filter.date);
    const hasFrom = hasNonEmptyDateString(filter.fromDate);
    const hasTo = hasNonEmptyDateString(filter.toDate);

    if (hasDate && (hasFrom || hasTo)) {
        return {
            ok: false,
            message: 'Use either date or fromDate/toDate, not both.',
            invalidElementIds: [...MATERIAL_SUMMARY_DATE_FIELD_IDS],
        };
    }

    if (!hasDate && !hasFrom && !hasTo) {
        return {
            ok: false,
            message: 'date or fromDate/toDate is required.',
            invalidElementIds: [...MATERIAL_SUMMARY_DATE_FIELD_IDS],
        };
    }

    if (hasFrom !== hasTo) {
        return {
            ok: false,
            message: 'Both fromDate and toDate are required for a date range.',
            invalidElementIds: [
                MATERIAL_SUMMARY_FROM_DATE_ELEMENT_ID,
                MATERIAL_SUMMARY_TO_DATE_ELEMENT_ID,
            ],
        };
    }

    const fromIso = hasDate ? nonEmptyString(filter.date) : nonEmptyString(filter.fromDate);
    const toIso = hasDate ? nonEmptyString(filter.date) : nonEmptyString(filter.toDate);

    if (!fromIso || !toIso) {
        return {
            ok: false,
            message: 'date or fromDate/toDate is required.',
            invalidElementIds: [...MATERIAL_SUMMARY_DATE_FIELD_IDS],
        };
    }

    if (!isValidDateOnly(fromIso) || !isValidDateOnly(toIso)) {
        return {
            ok: false,
            message: 'date and fromDate/toDate must be valid YYYY-MM-DD values.',
            invalidElementIds: [...MATERIAL_SUMMARY_DATE_FIELD_IDS],
        };
    }

    if (utcDayIndex(toIso) < utcDayIndex(fromIso)) {
        return {
            ok: false,
            message: 'fromDate must be less than or equal to toDate.',
            invalidElementIds: [
                MATERIAL_SUMMARY_FROM_DATE_ELEMENT_ID,
                MATERIAL_SUMMARY_TO_DATE_ELEMENT_ID,
            ],
        };
    }

    if (utcDayIndex(toIso) - utcDayIndex(fromIso) > 365) {
        return {
            ok: false,
            message: 'Date range cannot exceed 366 days.',
            invalidElementIds: [
                MATERIAL_SUMMARY_FROM_DATE_ELEMENT_ID,
                MATERIAL_SUMMARY_TO_DATE_ELEMENT_ID,
            ],
        };
    }

    if (filter.startIndex != null) {
        if (!Number.isInteger(filter.startIndex) || filter.startIndex < 0) {
            return {
                ok: false,
                message: 'startIndex must be greater than or equal to 0.',
                invalidElementIds: [],
            };
        }
    }

    if (filter.recordCount != null) {
        if (
            !Number.isInteger(filter.recordCount) ||
            !MATERIAL_SUMMARY_RECORD_COUNT_ALLOWED.includes(filter.recordCount)
        ) {
            return {
                ok: false,
                message: `recordCount must be one of: ${MATERIAL_SUMMARY_RECORD_COUNT_ALLOWED.join(', ')}.`,
                invalidElementIds: [MATERIAL_SUMMARY_RECORD_COUNT_ELEMENT_ID],
            };
        }
    }

    return { ok: true };
}

function hasNonEmptyDateString(value: string | null | undefined): boolean {
    return nonEmptyString(value) !== undefined;
}

function nonEmptyString(value: string | null | undefined): string | undefined {
    if (value == null) {
        return undefined;
    }
    const t = value.trim();
    return t.length > 0 ? t : undefined;
}

function isValidDateOnly(iso: string): boolean {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) {
        return false;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const dt = new Date(Date.UTC(y, mo - 1, d));
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

function utcDayIndex(iso: string): number {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) {
        return NaN;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    return Math.floor(Date.UTC(y, mo - 1, d) / 86_400_000);
}
